import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { shopperContext, type ShopperContextType } from '../../contexts/shopper'
import { breakpointContext } from '../../contexts/breakpoint'
import { BREAKPOINT } from '../../util/breakpoints'
import { STORAGE_KEY } from '../../util/storage-keys'
import { classMap } from 'lit/directives/class-map.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { formatCurrency } from '../../util/format'
import '../../util/tooltip'
import './vision-compare'
import { features as allFeatures } from '../../util/plans'
import { ApplicationService } from '../../services/application.service'
import { withBase } from '../../util/base-path'
import { getCostBreakdownDescription } from '../../util/cost-breakdown'
import { clearSelections } from '../../util/clear-selections'
import { orderVisionPlans } from '../../util/sort-plans'
import {
  isReadOnlyFromUrl,
  shouldDisablePlanAction,
} from '../../util/read-only-actions'
import { getPlanYear, getEffectiveDateFromContext } from '../../util/pdf-config'

interface PlanCard {
  planId: string
  planName: string
  monthlyPremium: string
  planType: 'dental' | 'vision'
  features: string[] | { display: string; value: any }[]
  applicantRate: any[]
  producerRecommended?: boolean
  rate?: any // For cost breakdown tooltip
  planRate?: any // For cost breakdown tooltip
}

@customElement('ancillary-vision-results')
export class AncillaryVisionResults extends LitElement {
  static styles = css`
    .container {
      border-radius: 4px;
      border: 1px solid var(--lh-color-neutral-200, #d1d3d4);
      box-shadow: 0px 8px 16px 0px rgba(0, 76, 108, 0.08);
    }
    .border {
      border: 3px solid var(--dark-primary-dark, #006894);
    }
    .plan-cards {
      display: flex;
      flex-direction: column;
      gap: 32px;
    }
    .desktop-wrapper {
      box-sizing: border-box;
      padding: 24px;
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 26px;
    }
    .mobile-wrapper {
      box-sizing: border-box;
      padding: 24px;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    .details {
      display: flex;
      flex-direction: column;
      gap: 24px;
      flex: 3;
    }
    .price {
      flex: 1;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      justify-content: center;
      background: var(--lh-color-primary-50, #ebf9ff);
      gap: 22px;
      padding: 24px;
    }
    .coverage-wrapper {
      display: flex;
      flex-direction: row;
      gap: 12px;
      flex-wrap: wrap;
    }
    .mobile-coverage-wrapper {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      flex-wrap: wrap;
      row-gap: 8px;
    }
    .coverage {
      display: flex;
      flex-direction: row;
      gap: 4px;
    }
    .copay-wrapper {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
    .mobile-copay-wrapper {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    .mobile-price-wrapper {
      display: flex;
    }
    .copay {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    .costs {
      display: flex;
      flex-direction: column;
      gap: 6px;
      align-items: center;
    }
    .price-buttons {
      display: flex;
      flex-direction: column;
      gap: 16px;
      align-items: center;
    }
    .price-text {
      white-space: pre-wrap;
    }
    .action-bar {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
    .recommend-bar {
      flex-direction: column;
      gap: 16px;
      align-items: start;
    }
    .hide {
      display: none;
    }
    .icon {
      color: white;
    }
    .top-banner {
      display: flex;
      flex-direction: row;
      gap: 8px;
    }
  `

  @consume({ context: shopperContext, subscribe: true })
  @state()
  shopperContextValue?: ShopperContextType

  @consume({ context: breakpointContext, subscribe: true })
  @state()
  breakpoint!: BREAKPOINT

  @state()
  isShopper: boolean = true

  @state()
  loading: boolean = false

  @state()
  selectedPanel: 'list' | 'compare' = 'list'

  private switchToListTab = () => {
    // Ensure internal state reflects list selection
    this.selectedPanel = 'list'
    this.requestUpdate()
    const tab = this.renderRoot?.querySelector(
      'lh-tab[panel="list"]'
    ) as HTMLElement | null
    if (tab) {
      tab.click()
    } else {
      const group = this.renderRoot?.querySelector(
        'lh-tab-group'
      ) as HTMLElement | null
      if (group) {
        group.setAttribute('default-panel', 'list')
      }
    }
  }

  private baseUrl: string = ''
  private brochureLink: string = ''

  private syncLoadingFromContext() {
    const hasData = !!this.shopperContextValue?.quoteData
    const wasLoading = this.loading
    this.loading = !hasData
    if (hasData) {
      this.selectedPanel = 'list'
    }
    // Force update if loading state changed
    if (wasLoading !== this.loading) {
      this.requestUpdate()
    }
  }

  private handleQuoteUpdated = () => {
    this.loading = false
    this.selectedPanel = 'list'
    this.requestUpdate()
  }

  getVisionDetailByKey(plan: any, key: any) {
    if (!plan || !plan.details) {
      return 'N/A'
    }

    if (plan.bluecrossncPartNumber === 'VIS1-PL2') {
      if (key === 'framesLensesContacts') {
        let txt = ''
        if (plan.details['frames'] === undefined) {
          txt = txt + 'N/A' + ', '
        } else {
          txt = txt + plan.details['frames'] + ', '
        }
        if (plan.details['standardLenses'] === undefined) {
          txt = txt + 'N/A' + ' or '
        } else {
          txt = txt + plan.details['standardLenses'] + ' or '
        }
        if (plan.details['contactLenses'] === undefined) {
          txt = txt + 'N/A'
        } else {
          txt = txt + plan.details['contactLenses']
        }
        return txt
      } else {
        if (plan.details[key] === undefined) {
          return 'N/A'
        } else {
          return plan.details[key]
        }
      }
    } else {
      if (plan.details[key] === undefined) {
        return 'N/A'
      } else {
        return plan.details[key]
      }
    }
  }

  public toPlanCard(plan: any): PlanCard {
    // Filter features from plans.ts where list='y', planCode='VIS1', and planName matches planId
    const planFeatures = allFeatures
      .filter(
        (f) => f.list === 'y' && f.planCode === 'VIS1' && f.planName === plan.id
      )
      .map((f) => ({
        display: f.display,
        value: plan.details?.[f.key] || f.defaultValue || 'N/A',
      }))

    return {
      planId: plan.id,
      planName: plan.description,
      monthlyPremium:
        typeof plan.planRate?.monthlyPremium === 'number'
          ? plan.planRate.monthlyPremium.toFixed(2)
          : String(plan.planRate?.monthlyPremium || ''),
      planType:
        String(plan.lob || '').toUpperCase() === 'VIS1' ? 'vision' : 'dental',
      features: planFeatures,
      applicantRate: plan.applicantRate,
      producerRecommended: !!plan.producerRecommended,
      planRate: plan.planRate, // Include planRate object for cost breakdown tooltip
    }
  }
  private topBanner(plan: PlanCard) {
    if (!plan.producerRecommended) return null
    let label
    if (plan.producerRecommended) {
      label = !this.shopperContextValue?.isAgent
        ? 'Your agent recommended this plan.'
        : 'Recommended plan'
    } else {
      label = 'You originally applied for this plan.'
    }
    return html`<lh-container
      theme="primary-dark"
      spacing="none"
      content-width="fluid"
      padding-top="sm"
      padding-bottom="sm"
      gutters="sm"
      ><div class="top-banner">
        <span class="icon">
          <lh-icon name="seal-check"></lh-icon>
        </span>
        <lh-typography>${label}</lh-typography>
      </div></lh-container
    >`
  }

  private handleRecommendedPlan(planId: string) {
    const quote = this.shopperContextValue?.quoteData
    const plans = quote?.individualPlans || []
    const idx = plans.findIndex(
      (p: any) => p.id === planId || p.planId === planId
    )
    if (idx < 0) return
    plans.forEach((p: any) => (p.producerRecommended = false))
    const selected = plans[idx]
    ;(selected as any).producerRecommended = true
    plans.splice(idx, 1)
    plans.unshift(selected)
    this.requestUpdate()

    // Ensure other components reflect the updated recommendation state
    document.dispatchEvent(
      new CustomEvent('quote-data-updated', {
        detail: { quoteData: this.shopperContextValue?.quoteData },
      })
    )

    // Do not auto-navigate to dental; dental is shown first now.
    // Stay on the current panel and let the user proceed.
  }

  connectedCallback() {
    super.connectedCallback()
    if (typeof window !== 'undefined') {
      if (
        window.location.host.includes('shopperstage.bcbsnc.com') ||
        window.location.host.includes('localhost')
      ) {
        this.baseUrl = 'https://assetsps.bcbsnc.com/assets/email'
      } else if (window.location.host.includes('shopper.bcbsnc.com')) {
        this.baseUrl = 'https://assets.bcbsnc.com/assets/email'
      } else {
        this.baseUrl = ''
      }
    }
    this.updateBrochureLink()
    // Initialize loading based on current context and listen for quote updates
    this.syncLoadingFromContext()
    document.addEventListener('quote-data-updated', this.handleQuoteUpdated)
  }

  private updateBrochureLink() {
    this.brochureLink =
      getPlanYear(getEffectiveDateFromContext(this.shopperContextValue)) ===
      '2027'
        ? withBase('/images/U35521_2027_Dental_Vision_Combo_Brochure_508.pdf')
        : this.baseUrl +
          '/2026/U35521_Dental_Vision_combo_brochure_for_individual_Final_508.pdf'
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    document.removeEventListener('quote-data-updated', this.handleQuoteUpdated)
  }

  firstUpdated(): void {
    // Double-check loading state after initial render in case
    // context was populated before the event listener attached.
    this.syncLoadingFromContext()
  }

  updated(changedProps: Map<string | number | symbol, unknown>): void {
    super.updated(changedProps as any)
    if (changedProps.has('shopperContextValue')) {
      this.syncLoadingFromContext()
      this.updateBrochureLink()
    }
  }

  private handleEditQuote() {
    // Return to previous step using browser history
    window.history.back()
  }

  private handleSelectPlan(planId: string) {
    // For both shopper and agent flows, centralize behavior in apply()
    this.apply(planId)
  }

  private getPlanMonthlyRate(plan: any): string {
    const rawRate = Array.isArray(plan?.planRate)
      ? plan?.planRate?.[0]?.monthlyPremium
      : plan?.planRate?.monthlyPremium

    if (rawRate === undefined || rawRate === null || rawRate === '') {
      return ''
    }

    return String(rawRate)
  }

  private submitImmediateBinderForm(visionPlanId: string): boolean {
    const quote = this.shopperContextValue?.quoteData
    const visionPlan = (quote?.individualPlans || []).find(
      (p: any) => p.id === visionPlanId || p.planId === visionPlanId
    )

    if (!visionPlan) {
      console.error('Vision plan not found for immediate-binder:', visionPlanId)
      return false
    }

    const dentalPlanId = sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_ID)
    const dentalSelected =
      sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_SELECTED) === 'true' &&
      !!dentalPlanId
    const dentalPlan = dentalSelected
      ? (quote?.crossSalePlans || quote?.jsonPlans || []).find(
          (p: any) => p.planId === dentalPlanId || p.id === dentalPlanId
        )
      : null

    const form = document.createElement('form')
    form.method = 'post'
    form.action = `${import.meta.env.VITE_BCBSNC_URL}/sapps/buyonline/application/IBinderPayment_ancillaryApplicants.action`
    form.style.display = 'none'

    const appendHiddenInput = (name: string, id: string, value: string) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.id = id
      input.value = value
      form.appendChild(input)
    }

    appendHiddenInput(
      'immediateBinderPayment.isDental',
      'isDental',
      dentalPlan ? 'true' : 'false'
    )
    appendHiddenInput('immediateBinderPayment.isVision', 'isVision', 'true')
    appendHiddenInput(
      'immediateBinderPayment.iBDentalApp.planName',
      'dentalPlan',
      String(dentalPlan?.description || '')
    )
    appendHiddenInput(
      'immediateBinderPayment.iBVisionApp.planName',
      'visionPLan',
      String(visionPlan?.description || '')
    )
    appendHiddenInput(
      'immediateBinderPayment.iBDentalApp.planRate',
      'dentalRate',
      this.getPlanMonthlyRate(dentalPlan)
    )
    appendHiddenInput(
      'immediateBinderPayment.iBVisionApp.planRate',
      'visionRate',
      this.getPlanMonthlyRate(visionPlan)
    )
    appendHiddenInput(
      'immediateBinderPayment.iBDentalApp.planID',
      'dentalPlanID',
      String((dentalPlan as any)?.id || dentalPlan?.planId || '')
    )
    appendHiddenInput(
      'immediateBinderPayment.iBVisionApp.planID',
      'visionPlanID',
      String(visionPlan?.id || visionPlanId)
    )

    document.body.appendChild(form)
    form.submit()
    return true
  }

  private handleSelectedPlans() {
    // Check if dental was selected - if not, mark it as skipped for vision-only email flow
    try {
      const dentalSelected = sessionStorage.getItem(
        STORAGE_KEY.DENTAL_PLAN_SELECTED
      )
      if (dentalSelected !== 'true') {
        sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_SELECTED, 'false')
      }
    } catch {
      // non-blocking
    }

    // Navigate to Producer Email Entry page
    window.history.pushState(
      {},
      '',
      withBase('producer-email-entry') +
        window.location.search +
        window.location.hash
    )
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  private apply(planId: string) {
    // Update plan in result
    this.updatePlan(planId)
    // Persist vision selection
    try {
      sessionStorage.setItem(STORAGE_KEY.VISION_PLAN_SELECTED, 'true')
      sessionStorage.setItem(STORAGE_KEY.VISION_PLAN_ID, planId)
    } catch {
      // non-blocking
    }

    const isImmediateBinderFlow =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    if (isImmediateBinderFlow) {
      this.submitImmediateBinderForm(planId)
      return
    }

    // If a dental plan was previously selected (from dental page), submit both now
    const dentalSelected =
      sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_SELECTED) === 'true'
    const dentalPlanId = sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_ID)
    if (dentalSelected && dentalPlanId) {
      // Build shopping cart with both plans and submit
      const quote = this.shopperContextValue?.quoteData
      const visionPlan = (quote?.individualPlans || []).find(
        (p: any) => p.id === planId || p.planId === planId
      )
      const dentalPlan = (quote?.crossSalePlans || quote?.jsonPlans || []).find(
        (p: any) => p.planId === dentalPlanId || p.id === dentalPlanId
      )

      // Fallback: if dental plan is missing, clear stale selection and submit vision-only.
      if (!visionPlan || !dentalPlan) {
        if (!visionPlan) {
          console.error(
            'Vision plan not found; submitting vision-only: ' + planId
          )
        }
        if (!dentalPlan) {
          console.warn(
            'Dental plan not found; clearing stale dental selection: ' +
              dentalPlanId
          )
          clearSelections({ dental: true })
        }
        this.doApply(planId, true)
        return
      }

      const shoppingCart = [
        { planId: visionPlan.id, plan: visionPlan },
        { planId: dentalPlan.planId, plan: dentalPlan },
      ]

      ApplicationService.submitApplication(
        quote,
        shoppingCart,
        false, // skipVision
        false, // skipDental
        '',
        this.shopperContextValue?.producerSession || null
      )

      // Clean up session selections
      clearSelections({ vision: true, dental: true })
      return
    }

    // Submit immediately with vision only when dental hasn't been selected
    this.doApply(planId, true)
  }

  private updatePlan(planId: string) {
    const plans = this.shopperContextValue?.quoteData?.individualPlans || []
    for (const plan of plans) {
      if (plan.id === planId) {
        // Update applicant rates if needed
        if (
          plan.applicantRate &&
          this.shopperContextValue?.quoteData?.quoteApplication?.applicants
        ) {
          plan.applicantRate.forEach((rate: any, index: number) => {
            if (
              this.shopperContextValue?.quoteData?.quoteApplication.applicants[
                index
              ]
            ) {
              this.shopperContextValue.quoteData.quoteApplication.applicants[
                index
              ].individualRate = rate.monthlyPremium
            }
          })
        }
        break
      }
    }
  }

  private doApply(planId: string, visionOnly: boolean = false) {
    // Add selected plan to shopping cart
    const plan = this.shopperContextValue?.quoteData?.individualPlans?.find(
      (p: any) => p.id === planId || p.planId === planId
    )

    if (!plan) {
      console.error('Plan not found:', planId)
      return
    }

    const shoppingCart = [
      {
        planId: plan.id,
        plan: plan,
      },
    ]

    // Submit application
    ApplicationService.submitApplication(
      this.shopperContextValue?.quoteData,
      shoppingCart,
      false, // skipVision
      visionOnly, // skipDental when submitting vision-only
      '', // campaignId
      this.shopperContextValue?.producerSession || null
    )
    // Clear only vision selection when submitting vision-only
    // so dental selection remains available for later combined submission.
    clearSelections({ vision: true })
  }

  // private handleSkipToDental() {
  //   // Mark vision as skipped for dental flow
  //   sessionStorage.setItem('visionPlanSelected', 'false')
  //   sessionStorage.removeItem('visionPlanId')
  //   window.history.pushState(
  //     {},
  //     '',
  //     withBase('dental-result') + window.location.search + window.location.hash
  //   )
  //   window.dispatchEvent(new PopStateEvent('popstate'))
  // }
  private handleSkipToDental() {
    // Mark vision as skipped and submit dental-only using prior selection
    try {
      sessionStorage.setItem(STORAGE_KEY.VISION_PLAN_SELECTED, 'false')
      sessionStorage.removeItem(STORAGE_KEY.VISION_PLAN_ID)
    } catch {
      // non-blocking
    }

    const quote = this.shopperContextValue?.quoteData
    const dentalPlanId = sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_ID)
    const dentalPlan = (quote?.crossSalePlans || quote?.jsonPlans || []).find(
      (p: any) => p.planId === dentalPlanId || p.id === dentalPlanId
    )

    // Check for immediate-binder flow - submit to ancillary applicants action
    const isImmediateBinder =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    if (isImmediateBinder) {
      const form = document.createElement('form')
      form.method = 'post'
      form.action = `${import.meta.env.VITE_BCBSNC_URL}/sapps/buyonline/application/IBinderPayment_ancillaryApplicants.action`
      form.style.display = 'none'

      const appendHiddenInput = (name: string, id: string, value: string) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = name
        input.id = id
        input.value = value
        form.appendChild(input)
      }

      appendHiddenInput(
        'immediateBinderPayment.isDental',
        'isDental',
        dentalPlan ? 'true' : 'false'
      )
      appendHiddenInput('immediateBinderPayment.isVision', 'isVision', 'false')
      appendHiddenInput(
        'immediateBinderPayment.iBDentalApp.planName',
        'dentalPlan',
        String(dentalPlan?.description || '')
      )
      appendHiddenInput(
        'immediateBinderPayment.iBVisionApp.planName',
        'visionPLan',
        ''
      )
      appendHiddenInput(
        'immediateBinderPayment.iBDentalApp.planRate',
        'dentalRate',
        this.getPlanMonthlyRate(dentalPlan)
      )
      appendHiddenInput(
        'immediateBinderPayment.iBVisionApp.planRate',
        'visionRate',
        ''
      )
      appendHiddenInput(
        'immediateBinderPayment.iBDentalApp.planID',
        'dentalPlanID',
        String((dentalPlan as any)?.id || dentalPlan?.planId || '')
      )
      appendHiddenInput(
        'immediateBinderPayment.iBVisionApp.planID',
        'visionPlanID',
        ''
      )

      document.body.appendChild(form)
      form.submit()
      return
    }

    // Check if delivery method is email - if so, navigate to producer email page
    const isEmail =
      this.shopperContextValue?.formData?.deliveryMethod === 'e' ||
      (this.shopperContextValue?.quoteData?.quoteApplication?.bolApplication &&
        (
          this.shopperContextValue.quoteData.quoteApplication
            .bolApplication as any
        )['deliveryMethod'] === 'e')

    if (isEmail) {
      // Navigate to Producer Email Entry page for email delivery
      window.history.pushState(
        {},
        '',
        withBase('producer-email-entry') +
          window.location.search +
          window.location.hash
      )
      window.dispatchEvent(new PopStateEvent('popstate'))
      return
    }

    if (!dentalPlanId || !quote) {
      // Fallback: if dental plan not found in session, navigate to dental page
      window.history.pushState(
        {},
        '',
        withBase('dental-result') +
          window.location.search +
          window.location.hash
      )
      window.dispatchEvent(new PopStateEvent('popstate'))
      return
    }

    if (!dentalPlan) {
      // If the dental plan object isn't loaded, navigate back to dental selection
      window.history.pushState(
        {},
        '',
        withBase('dental-result') +
          window.location.search +
          window.location.hash
      )
      window.dispatchEvent(new PopStateEvent('popstate'))
      return
    }

    const shoppingCart = [
      {
        planId: dentalPlan.planId,
        plan: dentalPlan,
      },
    ]

    ApplicationService.submitApplication(
      quote,
      shoppingCart,
      true, // skipVision
      false, // do not skip dental
      '',
      this.shopperContextValue?.producerSession || null
    )
    // Clear selections after successful dental-only submission
    clearSelections({ dental: true, vision: true })
  }

  get isMobile() {
    return (
      this.breakpoint === BREAKPOINT.PHONE ||
      this.breakpoint === BREAKPOINT.TABLET
    )
  }

  private get isReadOnlyMode() {
    return isReadOnlyFromUrl()
  }

  private get hasVisionPlans() {
    const visionPlans =
      this.shopperContextValue?.quoteData?.individualPlans || []
    return visionPlans.length > 0
  }

  private get hasDentalPlans() {
    const quote = this.shopperContextValue?.quoteData
    const dentalPlans = quote?.crossSalePlans || quote?.jsonPlans || []
    return dentalPlans.length > 0
  }

  private get disableVisionActionButton() {
    return shouldDisablePlanAction({
      readOnly: this.isReadOnlyMode,
      product: 'vision',
      hasDentalPlans: this.hasDentalPlans,
      hasVisionPlans: this.hasVisionPlans,
    })
  }

  // cost breakdown moved to shared util

  private getActionButton(plan: PlanCard, size: 'md' | 'lg') {
    const isEmail =
      this.shopperContextValue?.formData?.deliveryMethod === 'e' ||
      (this.shopperContextValue?.quoteData?.quoteApplication?.bolApplication &&
        (
          this.shopperContextValue.quoteData.quoteApplication
            .bolApplication as any
        )['deliveryMethod'] === 'e')
    const isImmediateBinder =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    const analyticsIdSuffix = isImmediateBinder ? ':immediate-binder' : ''
    const shouldDisableAction = this.disableVisionActionButton
    return isEmail
      ? html`<lh-button
          variant="filled"
          size=${size}
          full-width
          icon="seal-check"
          icon-position="left"
          ?disabled=${shouldDisableAction}
          analytics-id="ancillary-rate-quote:vision-results:${plan.producerRecommended
            ? 'keep-this-plan'
            : 'recommend-plan'}${analyticsIdSuffix}:button-text"
          @click=${() => this.handleRecommendedPlan(plan.planId)}
        >
          ${plan.producerRecommended ? 'Keep this plan' : 'Recommend Plan'}
        </lh-button>`
      : html`<lh-button
          variant="filled"
          size=${size}
          icon-position="left"
          full-width
          icon="note-pencil"
          ?disabled=${shouldDisableAction}
          analytics-id="ancillary-rate-quote:vision-results:${plan.producerRecommended
            ? 'keep-this-plan'
            : 'choose-plan'}${analyticsIdSuffix}:button-text"
          @click=${() => this.handleSelectPlan(plan.planId)}
        >
          ${plan.producerRecommended ? 'Keep this plan' : 'Choose Plan'}
        </lh-button>`
  }

  private renderPlanCard(plan: PlanCard) {
    const isImmediateBinder =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    return html`
      <div
        class=${classMap({
          container: true,
          border: !!plan.producerRecommended,
        })}
      >
        ${this.topBanner(plan)}
        <div
          slot="content"
          class=${classMap({
            'desktop-wrapper': !this.isMobile,
            'mobile-wrapper': this.isMobile,
          })}
        >
          <div class="details">
            <lh-image
              src="${new URL('../../images/vision_blue.png', import.meta.url)
                .href}"
              alt="${plan.planName}"
            ></lh-image>
            <div>
              <lh-typography size="lg" weight="700">
                ${plan.planName}
              </lh-typography>
            </div>
            ${this.isMobile
              ? html`<div>
                  ${isImmediateBinder
                    ? html`<lh-typography type="heading" alignment="left"
                        >${formatCurrency(plan.monthlyPremium)}</lh-typography
                      >`
                    : html`<rq-tooltip
                        .description=${getCostBreakdownDescription(
                          plan,
                          this.shopperContextValue?.quoteData,
                          !!this.shopperContextValue?.isAgent
                        )}
                        position="top"
                      >
                        <lh-typography type="heading" alignment="left"
                          >${formatCurrency(plan.monthlyPremium)}</lh-typography
                        ></rq-tooltip
                      >`}
                </div>`
              : ``}
            ${plan.features &&
            Array.isArray(plan.features) &&
            plan.features.length > 0
              ? html`<div class="copay-wrapper">
                  ${plan.features.map(
                    (feature: any) => html`
                      <div class="copay">
                        <lh-typography size="text-lg" alignment="left">
                          <strong class="price-text"
                            >${typeof feature === 'string'
                              ? feature
                              : unsafeHTML(feature.display)}</strong
                          ><br />
                          <span class="price-text"
                            >${typeof feature === 'string'
                              ? ''
                              : unsafeHTML(feature.value)}</span
                          >
                        </lh-typography>
                      </div>
                    `
                  )}
                </div>`
              : ``}
            <div
              class=${classMap({
                'coverage-wrapper': !this.isMobile,
                'mobile-coverage-wrapper': this.isMobile,
              })}
            >
              <lh-typography variant="label" size="label-sm"
                >Check coverage in this plan</lh-typography
              >
              <div class="coverage">
                ${(() => {
                  const planYear = getPlanYear(
                    getEffectiveDateFromContext(this.shopperContextValue)
                  )
                  const productOn = plan.planType
                  let href = ''
                  const baseUrl = this.baseUrl || ''
                  if (productOn === 'vision') {
                    if (plan.planId === 'VIS1-PL1') {
                      href =
                        planYear === '2027'
                          ? withBase(
                              '/images/U21496a_Blue_20-20_Vision_Benefit_Summaries_508.pdf'
                            )
                          : baseUrl +
                            '/2026/U21496a_Individual_Vision_Benefit_Summaries_508 Final.pdf'
                    } else if (plan.planId === 'VIS1-PL2') {
                      href =
                        planYear === '2027'
                          ? withBase(
                              '/images/U21496b_Blue_20-20_Vision_Benefit_Summaries_508.pdf'
                            )
                          : baseUrl +
                            '/2026/U21496b_Individual_Vision_Benefit_Summaries_508 Final.pdf'
                    }
                  }
                  return href
                    ? html`<lh-button-group size="sm"
                        ><lh-button
                          target="_blank"
                          variant="text"
                          size="sm"
                          href="${href}"
                          analytics-id="ancillary-rate-quote:vision-results:benefits-and-coverage-link:button-text"
                          >Summary of Benefits and Coverage (PDF)</lh-button
                        ><lh-button
                          target="_blank"
                          variant="text"
                          size="sm"
                          href="${this.brochureLink}"
                          analytics-id="ancillary-rate-quote:vision-results:product-brochure-link:button-text"
                        >
                          Product Brochure (PDF)
                        </lh-button></lh-button-group
                      >`
                    : null
                })()}
              </div>
            </div>
          </div>
          ${this.isMobile
            ? html` <div class="price-buttons">
                  ${this.getActionButton(plan, 'lg')}
                </div>
              </div>`
            : ``}
          ${!this.isMobile
            ? html`<div class="price">
                <div class="costs">
                  <lh-typography size="text-sm" alignment="center"
                    >Cost per month</lh-typography
                  >
                  <div class=${classMap({ 'full-width': this.isMobile })}>
                    ${isImmediateBinder
                      ? html`<lh-typography type="heading" alignment="center"
                          >${formatCurrency(plan.monthlyPremium)}</lh-typography
                        >`
                      : html`<rq-tooltip
                          .description=${getCostBreakdownDescription(
                            plan,
                            this.shopperContextValue?.quoteData,
                            !!this.shopperContextValue?.isAgent
                          )}
                          position="left"
                        >
                          <lh-typography type="heading" alignment="center"
                            >${formatCurrency(
                              plan.monthlyPremium
                            )}</lh-typography
                          ></rq-tooltip
                        >`}
                  </div>
                </div>
                <div class="price-buttons">
                  ${this.getActionButton(plan, 'md')}
                </div>
              </div>`
            : ''}
        </div>
      </div>
    `
  }

  render() {
    // Show skip button if cross-sale dental plans are available (crossSalePlans or jsonPlans)
    const showSkipButton =
      (this.shopperContextValue?.quoteData?.crossSalePlans &&
        this.shopperContextValue.quoteData.crossSalePlans.length > 0) ||
      (this.shopperContextValue?.quoteData?.jsonPlans &&
        this.shopperContextValue.quoteData.jsonPlans.length > 0)
    // Check dental selection status - could be 'true', 'false', or null
    const dentalPlanSelectedValue = sessionStorage.getItem(
      STORAGE_KEY.DENTAL_PLAN_SELECTED
    )
    const dentalSelected =
      dentalPlanSelectedValue === 'true' &&
      !!sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_ID)
    // Check if dental was explicitly skipped (set to 'false')
    const dentalSkipped = dentalPlanSelectedValue === 'false'
    // Also check if any dental plan has been marked as recommended
    const hasDentalRecommended = !!(
      this.shopperContextValue?.quoteData?.crossSalePlans ||
      this.shopperContextValue?.quoteData?.jsonPlans ||
      []
    ).some((p: any) => p.producerRecommended)
    const isAgent = !!this.shopperContextValue?.isAgent
    const referralSource =
      this.shopperContextValue?.quoteData?.quoteApplication?.bolApplication
        ?.referralSource
    const isProducerEntered = referralSource === 'PRODUCER_ENTERED'
    const isImmediateBinderFlow =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    // Only show "Skip Vision" if dental was selected or recommended (not skipped)
    // User must select at least one plan type
    const canSkip =
      !!showSkipButton &&
      !dentalSkipped &&
      (dentalSelected || hasDentalRecommended) &&
      (isAgent ||
        isProducerEntered ||
        new URLSearchParams(window.location.search).get('quoteId')) &&
      !isImmediateBinderFlow

    // Hide Back button if quote or quoteId param is present, unless it's vision-only
    const urlParams = new URLSearchParams(window.location.search)
    const hasQuoteParams = urlParams.get('quote') || urlParams.get('quoteId')
    const isVisionOnly = !showSkipButton // no dental plans available

    return html`
      <lh-container gutters="none" spacing="lg" context-id="vision-results">
        <lh-grid>
          <lh-grid-item>
            <lh-typography type="heading" size=${this.isMobile ? 'xl' : 'lg'}
              >Vision plan selection</lh-typography
            >
          </lh-grid-item>
        </lh-grid>
        <lh-tab-group default-panel=${this.selectedPanel}>
          <lh-tab slot="nav" panel="list">List View</lh-tab>
          <lh-tab slot="nav" panel="compare">Compare View</lh-tab>

          <lh-tab-panel name="list">
            ${this.loading
              ? html`<lh-loader></lh-loader>`
              : html`
                  <lh-container gutters="none" margin-bottom="lg">
                    <lh-typography
                      >Use the
                      <a
                        href="https://www.eyemedvisioncare.com/bcbsnc/public/provloc.emvc?networkId=108&amp;nwDDDisabled=true"
                        target="_blank"
                        >Find a Vision Provider tool<lh-icon
                          size="md"
                          name="arrow-square-out"
                        ></lh-icon
                      ></a>
                      to search the provider network before choosing a plan.
                    </lh-typography>
                    <lh-typography>
                      <strong>Note:</strong> Applicants who have had Blue 20/20
                      coverage within the past 12 months will be removed from
                      this application if they apply for Blue 20/20 for
                      Individuals coverage.
                    </lh-typography>
                    ${this.shopperContextValue?.formData?.deliveryMethod ===
                      'e' ||
                    (this.shopperContextValue?.quoteData?.quoteApplication
                      ?.bolApplication &&
                      (
                        this.shopperContextValue.quoteData.quoteApplication
                          .bolApplication as any
                      )['deliveryMethod'] === 'e')
                      ? html`<lh-button
                          variant="outlined"
                          icon-position="left"
                          icon="paper-plane-tilt"
                          @click=${this.handleSelectedPlans}
                          analaytics-id="ancillary-rate-quote:vision-results:email-delivery:send-selected-plans-to-my-client"
                          >Send Selected Plans to My Client</lh-button
                        >`
                      : ''}
                  </lh-container>
                  <div class="plan-cards">
                    ${(() => {
                      const plans =
                        this.shopperContextValue?.quoteData?.individualPlans ||
                        []
                      const ordered = orderVisionPlans(plans)
                      return ordered.map((plan: any) =>
                        this.renderPlanCard(this.toPlanCard(plan))
                      )
                    })()}
                  </div>
                `}
          </lh-tab-panel>
          <lh-tab-panel name="compare">
            <lh-container gutters="none">
              <rq-marketplace-compare
                @switch-to-list=${this.switchToListTab}
              ></rq-marketplace-compare>
            </lh-container>
          </lh-tab-panel>
        </lh-tab-group>
        <lh-button-group
          alignment="${this.isMobile
            ? 'center'
            : hasQuoteParams && isVisionOnly
              ? 'right'
              : 'auto'}"
          orientation="${this.isMobile ? 'vertical' : 'horizontal'}"
        >
          ${!(hasQuoteParams && isVisionOnly)
            ? html`<lh-button
                variant="text"
                icon="caret-left"
                icon-position="left"
                @click=${this.handleEditQuote}
                ?full-width=${this.isMobile}
                analytics-id="ancillary-rate-quote:vision-results:back-button:button-text"
              >
                Back
              </lh-button>`
            : ''}
          ${canSkip
            ? html`<lh-button
                @click=${this.handleSkipToDental}
                ?full-width=${this.isMobile}
                analytics-id="ancillary-rate-quote:vision-results:skip-vision-button${isImmediateBinderFlow
                  ? ':immediate-binder'
                  : ''}:button-text"
                >Skip Vision</lh-button
              >`
            : ''}
        </lh-button-group>
      </lh-container>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ancillary-vision-results': AncillaryVisionResults
  }
}
